import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRouter, useTranslate } from "@bluelibs/x-ui";
import * as Ant from "antd";
import dayjs from "dayjs";
import { forwardRef, useImperativeHandle, useState } from "react";
import { useQuery } from "@apollo/client";
import { EyeOutlined } from "@ant-design/icons";
import { AdminListItemRenderer } from "@bluelibs/x-ui-admin/dist/react/components";
import { FindUncompletedClientTransactions } from "@bundles/UIAppBundle/queries/FindUncompletedClientTransactions";
import { Routes } from "@bundles/UIAppBundle";
import IsTransactionCompletedCheckBox from "@bundles/UIAppBundle/components/IsTransactionCompletedCheckBox/IsTransactionCompletedCheckBox";
const YELLOW = "#FFA70E";
const RED = "#B03A2E";
export const ListUncompletedClientTransactionsWidget = forwardRef(({ startDate, endDate, bankAccountIds = [], companyIds = [], onTransactionView, }, ref) => {
    var _a, _b;
    const t = useTranslate();
    const router = useRouter();
    const [sortStatus, setSortStatus] = useState();
    const { data, loading, error, refetch } = useQuery(FindUncompletedClientTransactions, {
        variables: {
            input: {
                startDate,
                endDate,
                companyIds,
                bankAccountIds,
                options: { sort: sortStatus },
            },
        },
        fetchPolicy: "network-only",
    });
    useImperativeHandle(ref, () => ({
        refetchData() {
            refetch();
        },
    }));
    const columns = [
        {
            id: "external",
            title: t("management.transactions.fields.external"),
            key: "management.transactions.fields.external",
            dataIndex: ["external"],
            width: 275,
            render: (value, model) => {
                const props = {
                    type: "relation",
                    value,
                    relation: {
                        path: router.path(Routes.EXTERNALS_VIEW, {
                            params: {
                                id: value === null || value === void 0 ? void 0 : value._id,
                            },
                        }),
                        dataIndex: "name",
                    },
                };
                return _jsx(AdminListItemRenderer, Object.assign({}, props), void 0);
            },
        },
        {
            id: "dueDate",
            title: t("management.transactions.fields.dueDate"),
            key: "management.transactions.fields.dueDate",
            dataIndex: ["dueDate"],
            width: 400,
            sorter: (a, b, type) => {
                switch (type) {
                    case "ascend":
                        setSortStatus({ dueDate: 1 });
                        break;
                    case "descend":
                        setSortStatus({ dueDate: -1 });
                        break;
                }
            },
            render: (value, model) => {
                let style = {};
                let diff = dayjs(value).diff(new Date(), "days");
                if (diff < 0) {
                    diff = diff * -1;
                    if (diff >= 15 && diff <= 30) {
                        style = {
                            backgroundColor: YELLOW,
                            color: "white",
                            padding: "2px 5px",
                            borderRadius: 4,
                        };
                    }
                    if (diff > 30) {
                        style = {
                            backgroundColor: RED,
                            color: "white",
                            padding: "2px 5px",
                            borderRadius: 4,
                        };
                    }
                }
                else {
                    style = {
                        backgroundColor: "unset",
                        color: "black",
                    };
                }
                return (_jsxs("div", { children: [_jsx("p", Object.assign({ style: {
                                ...style,
                                margin: 0,
                            } }, { children: dayjs(value).format("MM/DD/YYYY") }), void 0), _jsx("p", Object.assign({ style: { fontSize: 12, margin: 0 } }, { children: dayjs(value).from(new Date()) }), void 0)] }, void 0));
            },
        },
        {
            id: "amount",
            title: t("management.transactions.fields.amount"),
            key: "management.transactions.fields.amount",
            dataIndex: ["amount"],
            sorter: (a, b, type) => {
                switch (type) {
                    case "ascend":
                        setSortStatus({ amount: 1 });
                        break;
                    case "descend":
                        setSortStatus({ amount: -1 });
                        break;
                }
            },
            render: (value, model) => {
                var _a;
                const props = {
                    type: "number",
                    value: `${(value !== null && value !== void 0 ? value : 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })} ${((_a = model === null || model === void 0 ? void 0 : model.sourceBankAccount) === null || _a === void 0 ? void 0 : _a.currency) || ""}`,
                };
                return _jsx(AdminListItemRenderer, Object.assign({}, props), void 0);
            },
        },
        {
            id: "company",
            title: t("management.transactions.fields.company"),
            key: "management.transactions.fields.company",
            dataIndex: ["company"],
            render: (value, model) => {
                const props = {
                    type: "relation",
                    value,
                    relation: {
                        path: router.path(Routes.COMPANIES_VIEW, {
                            params: {
                                id: value === null || value === void 0 ? void 0 : value._id,
                            },
                        }),
                        dataIndex: "name",
                    },
                };
                return _jsx(AdminListItemRenderer, Object.assign({}, props), void 0);
            },
        },
        {
            id: "transactionIdentifier",
            title: t("management.transactions.fields.transactionIdentifier"),
            key: "management.transactions.fields.transactionIdentifier",
            dataIndex: ["transactionIdentifier"],
            render: (value, model) => {
                const props = {
                    type: "string",
                    value,
                };
                return _jsx(AdminListItemRenderer, Object.assign({}, props), void 0);
            },
        },
        {
            id: "isCompleted",
            title: t("management.transactions.fields.isCompleted"),
            key: "management.transactions.fields.isCompleted",
            dataIndex: ["isCompleted"],
            render: (value, model) => {
                return _jsx(IsTransactionCompletedCheckBox, { value: value, model: model }, void 0);
            },
        },
        {
            title: t("management.transactions.action"),
            dataIndex: "",
            fixed: "right",
            render: (model) => (_jsx(Ant.Button, Object.assign({ onClick: () => { var _a; return onTransactionView((_a = model === null || model === void 0 ? void 0 : model._id) === null || _a === void 0 ? void 0 : _a.toString()); } }, { children: _jsx(EyeOutlined, {}, void 0) }), void 0)),
        },
    ];
    return (_jsxs(Ant.Card, Object.assign({ className: "page-transactions-list-widget uncompleted-transactions-container-table" }, { children: [_jsx("p", Object.assign({ className: "total-balances-container-table-header" }, { children: t("management.transactions.list.uncompleted_client_header") }), void 0), _jsx(Ant.Layout.Content, { children: _jsx(Ant.Table, { pagination: {
                        total: ((_a = data === null || data === void 0 ? void 0 : data.FindUncompletedClientTransactions) !== null && _a !== void 0 ? _a : []).length,
                        position: ["bottomLeft"],
                    }, loading: loading, columns: columns, dataSource: (_b = data === null || data === void 0 ? void 0 : data.FindUncompletedClientTransactions) !== null && _b !== void 0 ? _b : [], className: "page-uncompleted-client-transactions-list-widget" }, void 0) }, void 0)] }), void 0));
});
